import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    getSubdomain, listChats, deleteChat,
    listQuizzes, createQuiz, listQuizQuestions,
} from '@/services/api/chatbot';
import type { ChatbotChat, ChatbotQuiz } from '@/types/chatbot';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ArrowLeft, Plus, Trash2, Edit, Globe, Palette, Eye, MessageSquare, ShieldCheck, Settings, Sparkles, Layers, ExternalLink } from 'lucide-react';
import ChatFormModal from './components/ChatFormModal';
import ChatPreview from './components/ChatPreview';
import ThemePanel from './components/ThemePanel';
import QuestionList from './components/QuestionList';
import QuizEditModal from './components/QuizEditModal';
import ChatTemplateModal from './components/ChatTemplateModal';

export default function ChatBuilder() {
    const { subdomainId } = useParams<{ subdomainId: string }>();
    const subId = Number(subdomainId);
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [activeChatId, setActiveChatId] = useState<number | null>(null);
    const [chatFormOpen, setChatFormOpen] = useState(false);
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [editingChat, setEditingChat] = useState<ChatbotChat | null>(null);
    const [deleteChatConfirm, setDeleteChatConfirm] = useState<ChatbotChat | null>(null);
    const [editingQuiz, setEditingQuiz] = useState<ChatbotQuiz | null>(null);

    const { data: subdomain } = useQuery({
        queryKey: ['chatbot-subdomain', subId],
        queryFn: () => getSubdomain(subId),
        enabled: !!subId,
    });

    const { data: chats } = useQuery({
        queryKey: ['chatbot-chats', subId],
        queryFn: () => listChats(subId),
        enabled: !!subId,
    });

    const activeChat = chats?.find(c => c.id === activeChatId) || null;
    // se nada selecionado e existem chats, seleciona o primeiro
    if (!activeChatId && chats && chats.length > 0) {
        setActiveChatId(chats[0].id);
    }

    const { data: quizzes } = useQuery({
        queryKey: ['chatbot-quizzes', activeChatId],
        queryFn: () => listQuizzes(activeChatId!),
        enabled: !!activeChatId,
    });

    const [activeQuizId, setActiveQuizId] = useState<number | null>(null);
    if (!activeQuizId && quizzes && quizzes.length > 0) setActiveQuizId(quizzes[0].id);

    const { data: questions } = useQuery({
        queryKey: ['chatbot-questions', activeQuizId],
        queryFn: () => listQuizQuestions(activeQuizId!),
        enabled: !!activeQuizId,
    });

    const deleteChatMut = useMutation({
        mutationFn: deleteChat,
        onSuccess: () => {
            toast({ title: 'Chat deletado' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-chats', subId] });
            setActiveChatId(null);
            setDeleteChatConfirm(null);
        },
    });

    const createQuizMut = useMutation({
        mutationFn: () => createQuiz({ chat_id: activeChatId!, name: 'Novo quiz', weight: 1, is_active: true }),
        onSuccess: (q) => {
            queryClient.invalidateQueries({ queryKey: ['chatbot-quizzes', activeChatId] });
            setActiveQuizId(q.id);
            toast({ title: 'Quiz criado!' });
        },
    });

    if (!subdomain) {
        return <div className="p-6 text-slate-500">Carregando subdominio...</div>;
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
                <Button variant="ghost" size="sm" onClick={() => navigate('/chatbot')}>
                    <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="flex-1">
                    <h1 className="text-2xl font-semibold">{subdomain.subdomain}</h1>
                    <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
                        <span>Pai: <span className="font-mono">{subdomain.parent_domain?.domain || subdomain.parent_domain_id}</span></span>
                        {subdomain.ssl_status === 'valid' && (
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200">
                                <ShieldCheck className="w-3 h-3 mr-1" />SSL ok
                            </Badge>
                        )}
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-12 gap-6">
                {/* Sidebar: lista de chats */}
                <aside className="col-span-3">
                    <div className="bg-white border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold">Chats (paths)</h3>
                            <div className="flex items-center gap-1">
                                <Button size="sm" variant="ghost"
                                    onClick={() => setTemplateModalOpen(true)}
                                    title="Criar a partir de template">
                                    <Sparkles className="w-4 h-4 text-emerald-600" />
                                </Button>
                                <Button size="sm" variant="ghost"
                                    onClick={() => { setEditingChat(null); setChatFormOpen(true); }}
                                    title="Criar em branco">
                                    <Plus className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                        {chats?.length === 0 && (
                            <div className="text-xs text-slate-500 p-3 text-center border border-dashed rounded space-y-2">
                                <p>Sem chats ainda.</p>
                                <Button size="sm" variant="outline" className="w-full"
                                    onClick={() => setTemplateModalOpen(true)}>
                                    <Sparkles className="w-3 h-3 mr-1" />Usar template
                                </Button>
                            </div>
                        )}
                        <div className="space-y-1">
                            {chats?.map((c) => (
                                <button key={c.id}
                                    className={`w-full text-left px-3 py-2 rounded text-sm transition ${
                                        c.id === activeChatId
                                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                            : 'hover:bg-slate-50 border border-transparent'
                                    }`}
                                    onClick={() => { setActiveChatId(c.id); setActiveQuizId(null); }}>
                                    <div className="font-medium truncate">{c.name}</div>
                                    <div className="text-xs text-slate-500 font-mono truncate">{c.path}</div>
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* Conteudo */}
                <main className="col-span-9">
                    {!activeChat ? (
                        <div className="border border-dashed rounded-lg p-12 text-center text-slate-500">
                            Selecione ou crie um chat para comecar.
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-xl font-semibold truncate">{activeChat.name}</h2>
                                        <a
                                            href={`https://${subdomain.subdomain}${activeChat.path}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors"
                                            title={`Abrir https://${subdomain.subdomain}${activeChat.path}`}>
                                            <ExternalLink className="w-3 h-3" />
                                            Acessar
                                        </a>
                                    </div>
                                    <p className="text-sm text-slate-500 font-mono mt-0.5">
                                        <span className="text-slate-400">{subdomain.subdomain}</span>{activeChat.path}
                                    </p>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <Button variant="outline" size="sm"
                                        onClick={() => { setEditingChat(activeChat); setChatFormOpen(true); }}>
                                        <Edit className="w-4 h-4 mr-1" />Editar
                                    </Button>
                                    <Button variant="outline" size="sm"
                                        onClick={() => setDeleteChatConfirm(activeChat)}
                                        className="text-red-600">
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            <Tabs defaultValue="questions" className="w-full">
                                <TabsList>
                                    <TabsTrigger value="questions">
                                        <MessageSquare className="w-4 h-4 mr-1" />Perguntas
                                    </TabsTrigger>
                                    <TabsTrigger value="theme">
                                        <Palette className="w-4 h-4 mr-1" />Tema
                                    </TabsTrigger>
                                    <TabsTrigger value="ads">
                                        <Globe className="w-4 h-4 mr-1" />Ads
                                    </TabsTrigger>
                                    <TabsTrigger value="preview">
                                        <Eye className="w-4 h-4 mr-1" />Preview
                                    </TabsTrigger>
                                </TabsList>

                                <TabsContent value="questions" className="mt-4">
                                    {/* tabs internas de quiz */}
                                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                                        {quizzes?.map((q: ChatbotQuiz) => {
                                            const isActive = q.id === activeQuizId;
                                            return (
                                                <div key={q.id}
                                                    className={`inline-flex items-stretch rounded-full overflow-hidden border transition ${
                                                        isActive
                                                            ? 'bg-emerald-600 text-white border-emerald-600'
                                                            : 'bg-white text-slate-700 border-slate-200 hover:border-emerald-300'
                                                    }`}>
                                                    <button className="px-3 py-1 text-sm"
                                                        onClick={() => setActiveQuizId(q.id)}>
                                                        {q.name} {!q.is_active && '(off)'}
                                                        <span className="ml-1 opacity-60 text-xs">w={q.weight}</span>
                                                    </button>
                                                    <button
                                                        className={`px-2 border-l ${
                                                            isActive
                                                                ? 'border-emerald-500 hover:bg-emerald-700'
                                                                : 'border-slate-200 hover:bg-slate-50 text-slate-500'
                                                        }`}
                                                        onClick={(e) => { e.stopPropagation(); setEditingQuiz(q); }}
                                                        title="Editar quiz">
                                                        <Settings className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        <Button size="sm" variant="outline" onClick={() => createQuizMut.mutate()}>
                                            <Plus className="w-3 h-3 mr-1" />Quiz
                                        </Button>
                                    </div>

                                    {activeQuizId ? (
                                        <QuestionList
                                            quizId={activeQuizId}
                                            questions={questions || []}
                                        />
                                    ) : (
                                        <div className="text-slate-500 text-sm">Crie um quiz para comecar.</div>
                                    )}
                                </TabsContent>

                                <TabsContent value="theme" className="mt-4">
                                    <ThemePanel chat={activeChat} />
                                </TabsContent>

                                <TabsContent value="ads" className="mt-4">
                                    <AdsAndTranslationPanel chat={activeChat} subdomainId={subId} />
                                </TabsContent>

                                <TabsContent value="preview" className="mt-4">
                                    <ChatPreview subdomain={subdomain.subdomain} path={activeChat.path} />
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </main>
            </div>

            <ChatFormModal
                open={chatFormOpen}
                onOpenChange={setChatFormOpen}
                subdomainId={subId}
                editing={editingChat}
            />

            <ChatTemplateModal
                open={templateModalOpen}
                onOpenChange={setTemplateModalOpen}
                subdomainId={subId}
                onCreated={(chatId) => setActiveChatId(chatId)}
            />

            <ConfirmDialog
                open={!!deleteChatConfirm}
                onOpenChange={(o) => !o && setDeleteChatConfirm(null)}
                title="Deletar chat?"
                description={`Vai apagar ${deleteChatConfirm?.name} (${deleteChatConfirm?.path}) e todos os quizzes/perguntas dentro dele.`}
                confirmLabel="Deletar"
                variant="danger"
                onConfirm={() => deleteChatConfirm && deleteChatMut.mutate(deleteChatConfirm.id)}
            />

            {activeChatId && (
                <QuizEditModal
                    open={!!editingQuiz}
                    onOpenChange={(o) => !o && setEditingQuiz(null)}
                    quiz={editingQuiz}
                    chatId={activeChatId}
                    onDeleted={() => {
                        // se deletou o quiz ativo, limpa a selecao
                        if (editingQuiz?.id === activeQuizId) setActiveQuizId(null);
                    }}
                />
            )}
        </div>
    );
}

// painel de Ads - sempre auto-resolve do dominio pai
function AdsAndTranslationPanel({ chat, subdomainId }: { chat: ChatbotChat; subdomainId: number }) {
    const queryClient = useQueryClient();
    const { toast } = useToast();

    // Cascade granulation_id por slot - 3 estados:
    //   null  -> Padrao (usa as 26 prices auto-fetch da Rewarded cascade do dominio)
    //   0     -> Desativar cascade
    //   > 0   -> granulation personalizada
    const initGId = (raw: any): number | null =>
        raw === undefined || raw === null ? null : Number(raw);

    const [form, setForm] = useState<{
        ad_topo_every_n_questions: number;
        topo_cascade_granulation_id: number | null;
        interstitial_cascade_granulation_id: number | null;
        reward_cascade_granulation_id: number | null;
        p2_topo_cascade_granulation_id: number | null;
        p2_interstitial_cascade_granulation_id: number | null;
        p2_reward_cascade_granulation_id: number | null;
        topo_fallback_enabled: boolean;
        p2_topo_fallback_enabled: boolean;
        topo_show_after_reward: boolean;
        topo_show_before_reward: boolean;
        reward_retry_enabled: boolean;
        reward_allow_early_close: boolean;
    }>({
        ad_topo_every_n_questions: chat.ad_topo_every_n_questions || 0,
        topo_cascade_granulation_id: initGId((chat as any).topo_cascade_granulation_id),
        interstitial_cascade_granulation_id: initGId((chat as any).interstitial_cascade_granulation_id),
        reward_cascade_granulation_id: initGId((chat as any).reward_cascade_granulation_id),
        p2_topo_cascade_granulation_id: initGId((chat as any).p2_topo_cascade_granulation_id),
        p2_interstitial_cascade_granulation_id: initGId((chat as any).p2_interstitial_cascade_granulation_id),
        p2_reward_cascade_granulation_id: initGId((chat as any).p2_reward_cascade_granulation_id),
        topo_fallback_enabled: !!(chat as any).topo_fallback_enabled,
        p2_topo_fallback_enabled: !!(chat as any).p2_topo_fallback_enabled,
        topo_show_after_reward: !!chat.topo_show_after_reward,
        topo_show_before_reward: !!(chat as any).topo_show_before_reward,
        reward_retry_enabled: !!(chat as any).reward_retry_enabled,
        reward_allow_early_close: !!(chat as any).reward_allow_early_close,
    });

    // busca o subdomain pra ter o parent_domain_id
    const { data: subdomain } = useQuery({
        queryKey: ['chatbot-subdomain', subdomainId],
        queryFn: async () => {
            const { getSubdomain } = await import('@/services/api/chatbot');
            return getSubdomain(subdomainId);
        },
        enabled: !!subdomainId,
    });

    // busca o preview do auto-resolve
    const { data: resolved, isLoading: resolving, error: resolveError } = useQuery({
        queryKey: ['ad-units-resolve', subdomain?.parent_domain_id],
        queryFn: async () => {
            const { resolveAdUnits } = await import('@/services/api/chatbot');
            return resolveAdUnits(subdomain!.parent_domain_id);
        },
        enabled: !!subdomain?.parent_domain_id,
        retry: 0,
    });

    // lista de granulations disponiveis no ms-price-rules
    const { data: granulations, isLoading: granulationsLoading, error: granulationsError } = useQuery({
        queryKey: ['cascade-granulations'],
        queryFn: async () => {
            const { listCascadeGranulations } = await import('@/services/api/chatbot');
            return listCascadeGranulations();
        },
        retry: 0,
    });
    const granulationById = (id: number | null) =>
        id == null || id <= 0 ? undefined : granulations?.find(g => g.id === id);

    const saveMut = useMutation({
        mutationFn: async () => {
            const { updateChat } = await import('@/services/api/chatbot');
            return updateChat(chat.id, {
                // sempre auto-resolve do dominio pai (campos manuais ficam vazios)
                ad_topo_ad_unit_id: '',
                ad_interstitial_ad_unit_id: '',
                ad_reward_ad_unit_id: '',
                ad_topo_every_n_questions: Number(form.ad_topo_every_n_questions) || undefined,
                // granulation por slot - null/0/positivo enviados como sao
                topo_cascade_granulation_id: form.topo_cascade_granulation_id,
                interstitial_cascade_granulation_id: form.interstitial_cascade_granulation_id,
                reward_cascade_granulation_id: form.reward_cascade_granulation_id,
                // P2 (/low- retry) granulations - separadas das P1
                p2_topo_cascade_granulation_id: form.p2_topo_cascade_granulation_id,
                p2_interstitial_cascade_granulation_id: form.p2_interstitial_cascade_granulation_id,
                p2_reward_cascade_granulation_id: form.p2_reward_cascade_granulation_id,
                // Fallback no Topo (1 request sem price se cascade esgotar)
                topo_fallback_enabled: form.topo_fallback_enabled,
                p2_topo_fallback_enabled: form.p2_topo_fallback_enabled,
                topo_show_after_reward: form.topo_show_after_reward,
                topo_show_before_reward: form.topo_show_before_reward,
                reward_retry_enabled: form.reward_retry_enabled,
                reward_allow_early_close: form.reward_allow_early_close,
            } as any);
        },
        onSuccess: () => {
            toast({ title: 'Configuracoes salvas!' });
            queryClient.invalidateQueries({ queryKey: ['chatbot-chats'] });
        },
        onError: (e: any) => toast({
            variant: 'destructive', title: 'Erro ao salvar',
            description: e.response?.data?.detail || e.message,
        }),
    });

    const SLOT_LABELS = {
        topo: 'Topo',
        interstitial: 'Interstitial',
        rewarded: 'Reward',
    } as const;

    return (
        <div className="space-y-4">
            {/* Ad units do dominio pai (read-only) */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                        <Globe className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-slate-900 leading-tight">Ad units do dominio pai</h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Resolvidos automaticamente da estrutura GAM de{' '}
                            <span className="font-mono text-slate-700">{subdomain?.parent_domain?.domain || '...'}</span>.
                            Mobile/Web sao escolhidos pelo navegador do usuario.
                        </p>
                    </div>
                </div>

                {resolving && (
                    <div className="text-xs text-slate-500 py-3 text-center">Resolvendo ad_units...</div>
                )}
                {resolveError ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
                        Nao foi possivel resolver: {(resolveError as any)?.response?.data?.detail || (resolveError as any)?.message}
                        <div className="mt-1">Confirme que o GAM esta sincronizado para este dominio.</div>
                    </div>
                ) : resolved && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <div className="grid grid-cols-12 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                            <div className="col-span-3">Bloco</div>
                            <div className="col-span-4">Mobile</div>
                            <div className="col-span-5">Web</div>
                        </div>
                        {(['topo', 'interstitial', 'rewarded'] as const).map((k, i, arr) => (
                            <div key={k}
                                className={`grid grid-cols-12 px-3 py-2.5 text-xs items-center ${
                                    i < arr.length - 1 ? 'border-b border-slate-100' : ''
                                }`}>
                                <div className="col-span-3 font-medium text-slate-700">
                                    {SLOT_LABELS[k]}
                                </div>
                                <div className="col-span-4 font-mono text-[10px] text-slate-500 break-all pr-2">
                                    {resolved[k].mobile || <span className="text-red-500 not-italic">— nao cadastrado —</span>}
                                </div>
                                <div className="col-span-5 font-mono text-[10px] text-slate-500 break-all pr-2">
                                    {resolved[k].web || <span className="text-red-500 not-italic">— nao cadastrado —</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Comportamento do Topo */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                        <Eye className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-slate-900 leading-tight">Comportamento do Topo</h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Quando e como o bloco Topo aparece no fluxo do chat
                        </p>
                    </div>
                </div>

                <label className="flex items-start gap-3 mt-4 cursor-pointer">
                    <Switch checked={form.topo_show_after_reward}
                        onCheckedChange={(v) => setForm({ ...form, topo_show_after_reward: v })} />
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 leading-tight">
                            Mostrar Topo apenas apos o Reward
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            Topo aparece <strong>uma unica vez</strong>, na proxima pergunta apos o user fechar o reward (estilo Wivlox /iow).
                            Quando ativo, ignora os toggles abaixo.
                        </div>
                    </div>
                </label>

                {/* Toggle "antes do reward" so aparece quando o "apos reward" esta OFF */}
                {!form.topo_show_after_reward && (
                    <label className="flex items-start gap-3 mt-4 pt-4 border-t border-slate-100 cursor-pointer">
                        <Switch checked={form.topo_show_before_reward}
                            onCheckedChange={(v) => setForm({ ...form, topo_show_before_reward: v })} />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-slate-800 leading-tight">
                                Mostrar Topo <em>junto</em> com o Reward
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                Topo aparece <strong>atras do offerwall</strong> (o overlay e semi-transparente).
                                A impressao do topo conta mesmo sem o user assistir o reward, pq o creative ja
                                esta no DOM antes do overlay abrir. Substitui "a cada N perguntas".
                            </div>
                        </div>
                    </label>
                )}

                <label className="flex items-start gap-3 mt-4 pt-4 border-t border-slate-100 cursor-pointer">
                    <Switch checked={form.reward_retry_enabled}
                        onCheckedChange={(v) => setForm({ ...form, reward_retry_enabled: v })} />
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 leading-tight">
                            Retry de Reward em <span className="font-mono">/low-{chat.path.replace(/^\//, '')}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            Se o reward nao vender (cascade esgotada ou user fechou cedo), navega pra{' '}
                            <span className="font-mono">/low-{chat.path.replace(/^\//, '')}</span>
                            {' '}pra rodar a cascade de novo (fresh URL = nova chance de bidding).
                            Loader continua entre paginas; ao voltar, mostra o topo + proxima pergunta normalmente.
                        </div>
                    </div>
                </label>

                <label className="flex items-start gap-3 mt-4 pt-4 border-t border-slate-100 cursor-pointer">
                    <Switch checked={form.reward_allow_early_close}
                        onCheckedChange={(v) => setForm({ ...form, reward_allow_early_close: v })} />
                    <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 leading-tight">
                            Permitir fechar reward antes do fim
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            Se o user <strong>viu o ad</strong> (creative ficou 50% visivel por 1s) mas fechou
                            antes do final, conta como assistido. <strong>Pula o retry</strong> em{' '}
                            <span className="font-mono">/low-</span> e continua o chat direto. Bom pra reduzir
                            friccao quando o objetivo e CPM e nao completion garantido.
                        </div>
                    </div>
                </label>

                {/* "A cada N perguntas" so se TODOS os modos automaticos do topo estao OFF */}
                {!form.topo_show_after_reward && !form.topo_show_before_reward && (
                    <div className="mt-4 pt-4 border-t border-slate-100 max-w-xs">
                        <label className="text-xs font-medium text-slate-700">Bloco Topo a cada N perguntas</label>
                        <p className="text-[11px] text-slate-500 mb-1.5">0 = desativado</p>
                        <Input type="number"
                            value={String(form.ad_topo_every_n_questions || '')}
                            onChange={(e) => setForm({ ...form, ad_topo_every_n_questions: Number(e.target.value) })}
                            className="h-9 max-w-[140px]" />
                    </div>
                )}
            </div>

            {/* Cascata de Prices por Slot */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                        <Layers className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-semibold text-slate-900 leading-tight">Cascata de Prices por Slot</h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Cada slot usa <strong>Padrão</strong> (cascade auto do domínio) por default. Personalize com uma
                            granulation específica, ou desative se preferir single bid.
                        </p>
                    </div>
                </div>

                {granulationsLoading && (
                    <p className="text-xs text-slate-500">Carregando granulations...</p>
                )}
                {granulationsError ? (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-700">
                        Erro ao listar granulations: {(granulationsError as any)?.response?.data?.detail || (granulationsError as any)?.message}
                        <div className="mt-1">Cadastre presets em <strong>Gam &gt; Price Cascade &gt; Granulacoes</strong>.</div>
                    </div>
                ) : granulations && (
                    <div className="space-y-5">
                        {/* ===== P1: pagina principal /{path} ===== */}
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-bold uppercase tracking-wide text-emerald-700">P1</span>
                                <span className="text-xs text-slate-500">Pagina principal — <span className="font-mono">/{chat.path.replace(/^\//, '')}</span></span>
                            </div>
                            <div className="space-y-2">
                                <GranulationPicker
                                    label="Topo"
                                    value={form.topo_cascade_granulation_id}
                                    onChange={(v) => setForm({ ...form, topo_cascade_granulation_id: v })}
                                    granulations={granulations}
                                    preview={granulationById(form.topo_cascade_granulation_id)}
                                />
                                <label className="flex items-start gap-3 ml-24 cursor-pointer">
                                    <Switch checked={form.topo_fallback_enabled}
                                        onCheckedChange={(v) => setForm({ ...form, topo_fallback_enabled: v })} />
                                    <div className="text-xs text-slate-600">
                                        <span className="font-medium">Fallback no Topo</span> — se cascade esgotar,
                                        faz <strong>1 request sem price targeting</strong> (deixa o GAM servir qualquer demand).
                                    </div>
                                </label>
                                <GranulationPicker
                                    label="Interstitial"
                                    value={form.interstitial_cascade_granulation_id}
                                    onChange={(v) => setForm({ ...form, interstitial_cascade_granulation_id: v })}
                                    granulations={granulations}
                                    preview={granulationById(form.interstitial_cascade_granulation_id)}
                                />
                                <GranulationPicker
                                    label="Reward"
                                    value={form.reward_cascade_granulation_id}
                                    onChange={(v) => setForm({ ...form, reward_cascade_granulation_id: v })}
                                    granulations={granulations}
                                    preview={granulationById(form.reward_cascade_granulation_id)}
                                />
                            </div>
                        </div>

                        {/* ===== P2: pagina retry /low-{path} (so se reward_retry_enabled) ===== */}
                        {form.reward_retry_enabled && (
                            <div className="pt-4 border-t border-slate-100">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs font-bold uppercase tracking-wide text-violet-700">P2</span>
                                    <span className="text-xs text-slate-500">Retry — <span className="font-mono">/low-{chat.path.replace(/^\//, '')}</span></span>
                                </div>
                                <div className="space-y-2">
                                    <GranulationPicker
                                        label="Topo"
                                        value={form.p2_topo_cascade_granulation_id}
                                        onChange={(v) => setForm({ ...form, p2_topo_cascade_granulation_id: v })}
                                        granulations={granulations}
                                        preview={granulationById(form.p2_topo_cascade_granulation_id)}
                                    />
                                    <label className="flex items-start gap-3 ml-24 cursor-pointer">
                                        <Switch checked={form.p2_topo_fallback_enabled}
                                            onCheckedChange={(v) => setForm({ ...form, p2_topo_fallback_enabled: v })} />
                                        <div className="text-xs text-slate-600">
                                            <span className="font-medium">Fallback no Topo</span> — se cascade esgotar,
                                            faz <strong>1 request sem price targeting</strong>.
                                        </div>
                                    </label>
                                    <GranulationPicker
                                        label="Interstitial"
                                        value={form.p2_interstitial_cascade_granulation_id}
                                        onChange={(v) => setForm({ ...form, p2_interstitial_cascade_granulation_id: v })}
                                        granulations={granulations}
                                        preview={granulationById(form.p2_interstitial_cascade_granulation_id)}
                                    />
                                    <GranulationPicker
                                        label="Reward"
                                        value={form.p2_reward_cascade_granulation_id}
                                        onChange={(v) => setForm({ ...form, p2_reward_cascade_granulation_id: v })}
                                        granulations={granulations}
                                        preview={granulationById(form.p2_reward_cascade_granulation_id)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Sticky save */}
            <div className="sticky bottom-4 bg-white border rounded-lg p-3 flex items-center justify-between shadow-lg">
                <span className="text-sm text-slate-500">As mudancas precisam ser salvas pra aplicar</span>
                <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
                    {saveMut.isPending ? 'Salvando...' : 'Salvar configuracoes'}
                </Button>
            </div>
        </div>
    );
}

interface CascadeGranulationLite { id: number; name: string; steps: number[]; }

const DEFAULT_OPTION_VALUE = '__default__';
const DISABLED_OPTION_VALUE = '0';

function GranulationPicker({
    label, value, onChange, granulations, preview,
}: {
    label: string;
    value: number | null;             // null=Padrao, 0=Desativar, >0=granulation id
    onChange: (v: number | null) => void;
    granulations: CascadeGranulationLite[];
    preview?: CascadeGranulationLite; // so existe quando value > 0
}) {
    // converte form state -> string pro <select>
    const selectValue = value === null
        ? DEFAULT_OPTION_VALUE
        : String(value);

    const handleChange = (v: string) => {
        if (v === DEFAULT_OPTION_VALUE) onChange(null);
        else onChange(Number(v));
    };

    // badge visual de status
    const statusBadge =
        value === null ? { text: 'Padrão', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' }
        : value === 0 ? { text: 'Desativado', class: 'bg-slate-100 text-slate-600 border-slate-200' }
        : { text: 'Personalizado', class: 'bg-violet-100 text-violet-700 border-violet-200' };

    return (
        <div className="border rounded-lg p-3 bg-slate-50/50">
            <div className="flex items-center gap-3">
                <div className="w-24 flex flex-col gap-1 shrink-0">
                    <span className="text-sm font-semibold">{label}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium text-center ${statusBadge.class}`}>
                        {statusBadge.text}
                    </span>
                </div>
                <select value={selectValue}
                    onChange={(e) => handleChange(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-md text-sm bg-white h-10">
                    <option value={DEFAULT_OPTION_VALUE}>Padrão (cascade automática do domínio)</option>
                    <option value={DISABLED_OPTION_VALUE}>— Desativar cascade —</option>
                    {granulations.length > 0 && (
                        <optgroup label="Granulations personalizadas">
                            {granulations.map(g => (
                                <option key={g.id} value={g.id}>
                                    {g.name} ({g.steps.length} steps
                                    {g.steps.length > 0 && ` · $${g.steps[0].toFixed(2)} → $${g.steps[g.steps.length - 1].toFixed(2)}`})
                                </option>
                            ))}
                        </optgroup>
                    )}
                </select>
            </div>
            {/* Preview da granulation personalizada selecionada */}
            {value !== null && value > 0 && preview && preview.steps.length > 0 && (
                <div className="mt-2 ml-24 pl-3 border-l-2 border-violet-300">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Preview</div>
                    <div className="font-mono text-[11px] text-slate-700 break-words">
                        {preview.steps.map(s => s.toFixed(2)).join(' → ')}
                    </div>
                </div>
            )}
            {/* Hint pra opcao Padrao */}
            {value === null && (
                <div className="mt-2 ml-24 pl-3 border-l-2 border-emerald-300 text-[11px] text-slate-500">
                    Usa as ~26 prices da <strong>Rewarded cascade</strong> cadastrada pro domínio em <span className="font-mono">price-rules</span>.
                </div>
            )}
        </div>
    );
}
